import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield, Eye, Lock, Database, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  const lastUpdated = "January 25, 2025";

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
          <div className="flex items-center space-x-3 mb-4">
            <Shield className="w-8 h-8 text-primary" />
            <h1 className="text-4xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-muted-foreground">
            Last updated: {lastUpdated}
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-6">
          {/* Introduction */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Eye className="w-5 h-5" />
                <span>Our Commitment to Privacy</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                The State of Minnesota is committed to protecting privacy through CareKonnect's 
                email-only referral system. This Privacy Policy explains how we collect, use, protect, 
                and share information while avoiding storage of Protected Health Information (PHI) 
                and Personally Identifiable Information (PII).
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold mb-2 text-blue-900">No PHI/PII Storage</h4>
                <p className="text-sm text-blue-800">
                  CareKonnect operates as an email-only referral coordination platform. We do not 
                  store Protected Health Information (PHI) or Personally Identifiable Information 
                  about clients. All referrals are processed through users' email clients.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Information We Collect */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Database className="w-5 h-5" />
                <span>Information We Collect</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Account Information</h4>
                <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Name, email address, and professional role</li>
                  <li>Organization or facility name and address</li>
                  <li>Professional license numbers and NPI (for providers)</li>
                  <li>Phone numbers and contact information</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Facility and Listing Information</h4>
                <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Facility details, bed availability, and care services offered</li>
                  <li>Pricing information and waiver types accepted</li>
                  <li>Facility location and accessibility features</li>
                  <li>Minnesota Assisted Living Scorecard data (publicly available)</li>
                </ul>
              </div>
               <div>
                 <h4 className="font-semibold mb-2">Referral Metadata (No PHI/PII)</h4>
                 <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                   <li>General support area categories selected during referral initiation</li>
                   <li>Provider contact information and facility details</li>
                   <li>Referral status updates (accepted/declined/pending)</li>
                   <li>Date of referral initiation and outcome tracking</li>
                 </ul>
               </div>
              <div>
                <h4 className="font-semibold mb-2">Technical Information</h4>
                <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                  <li>IP addresses, browser type, and device information</li>
                  <li>Platform usage patterns and feature interactions</li>
                  <li>Login times and session duration</li>
                  <li>Error logs and system performance data</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* How We Use Information */}
          <Card>
            <CardHeader>
              <CardTitle>How We Use Your Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Platform Operations</h4>
                <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Facilitating care placement coordination between providers and professionals</li>
                  <li>Matching client needs with available bed listings</li>
                  <li>Processing referrals and enabling secure communication</li>
                  <li>Maintaining accurate facility and availability information</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Account Management</h4>
                <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Creating and maintaining user accounts and profiles</li>
                  <li>Verifying professional credentials and licensing status</li>
                  <li>Processing subscription payments for provider accounts</li>
                  <li>Providing technical support and customer service</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">System Improvement</h4>
                <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Analyzing usage patterns to improve platform functionality</li>
                  <li>Generating aggregated reports on placement trends (de-identified)</li>
                  <li>Conducting quality assurance and system optimization</li>
                  <li>Developing new features based on user needs</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Information Sharing */}
          <Card>
            <CardHeader>
              <CardTitle>Information Sharing and Disclosure</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Within the Platform</h4>
                <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Provider listings are visible to verified placement professionals</li>
                  <li>Referral information is shared only with the intended provider</li>
                  <li>Professional profiles are visible to facilitate communication</li>
                  <li>Facility scorecard data is displayed as publicly available information</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Government Agencies</h4>
                <p className="text-sm text-muted-foreground">
                  Information may be shared with other Minnesota state agencies for regulatory 
                  oversight, quality assurance, or as required by law. This includes the 
                  Department of Health and Department of Human Services.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Legal Requirements</h4>
                <p className="text-sm text-muted-foreground">
                  We may disclose information when required by law, court order, or to protect 
                  the safety of individuals or the integrity of the platform.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Data Security */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Lock className="w-5 h-5" />
                <span>Data Security and Protection</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Technical Safeguards</h4>
                <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                  <li>End-to-end encryption for all data transmission</li>
                  <li>Secure data storage with industry-standard encryption</li>
                  <li>Multi-factor authentication for account access</li>
                  <li>Regular security audits and vulnerability assessments</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Administrative Safeguards</h4>
                <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Role-based access controls limiting data access to authorized personnel</li>
                  <li>Regular staff training on privacy and security procedures</li>
                  <li>Incident response procedures for potential security breaches</li>
                  <li>Business Associate Agreements with third-party service providers</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Physical Safeguards</h4>
                <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Secure data centers with restricted access controls</li>
                  <li>Environmental monitoring and backup power systems</li>
                  <li>Secure disposal of physical media containing sensitive data</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* User Rights */}
          <Card>
            <CardHeader>
              <CardTitle>Your Rights and Choices</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Access and Correction</h4>
                <p className="text-sm text-muted-foreground">
                  You have the right to access and update your personal information through 
                  your account settings. For assistance, contact our support team.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Account Deactivation</h4>
                <p className="text-sm text-muted-foreground">
                  You may deactivate your account at any time. Upon deactivation, your 
                  personal information will be removed, though some records may be retained 
                  as required by law.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Communication Preferences</h4>
                <p className="text-sm text-muted-foreground">
                  You can manage email notification preferences through your account settings. 
                  However, certain transactional emails cannot be disabled.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Data Retention */}
          <Card>
            <CardHeader>
              <CardTitle>Data Retention</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                We retain personal information only as long as necessary to fulfill the purposes 
                outlined in this policy or as required by law. Specific retention periods include:
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                <li>Account information: Retained while account is active + 7 years</li>
                <li>Referral records: Retained for 7 years after referral completion</li>
                <li>Audit logs: Retained for 7 years for compliance purposes</li>
                <li>De-identified aggregate data: May be retained indefinitely for research</li>
              </ul>
            </CardContent>
          </Card>

          {/* Third-Party Services */}
          <Card>
            <CardHeader>
              <CardTitle>Third-Party Services</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Service Providers</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  We use trusted third-party services to support platform operations:
                </p>
                <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Supabase (database and authentication services)</li>
                  <li>Stripe (payment processing for provider subscriptions)</li>
                  <li>Resend (email delivery services)</li>
                </ul>
                <p className="text-sm text-muted-foreground mt-2">
                  All third-party providers are bound by Business Associate Agreements 
                  ensuring HIPAA compliance and data protection.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Breach Notification */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5" />
                <span>Breach Notification</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                In the event of a data breach that may compromise your personal information, 
                we will notify affected users within 72 hours of discovery, as required by 
                law. Notifications will include details about the breach, steps taken to 
                address it, and recommendations for protecting your information.
              </p>
            </CardContent>
          </Card>

          {/* Children's Privacy */}
          <Card>
            <CardHeader>
              <CardTitle>Children's Privacy</CardTitle>
            </CardHeader>
            <CardContent>
              <p>
                CareKonnect is intended for use by healthcare professionals and is not 
                directed to individuals under 18 years of age. We do not knowingly collect 
                personal information from children under 18.
              </p>
            </CardContent>
          </Card>

          {/* Policy Updates */}
          <Card>
            <CardHeader>
              <CardTitle>Policy Updates</CardTitle>
            </CardHeader>
            <CardContent>
              <p>
                We may update this Privacy Policy periodically to reflect changes in our 
                practices or legal requirements. Material changes will be communicated to 
                users via email or platform notifications 30 days prior to implementation.
              </p>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4">
                For questions about this Privacy Policy or to exercise your privacy rights, 
                please contact us:
              </p>
              <div className="space-y-2">
                <p><strong>Privacy Officer:</strong> CareKonnect Privacy Team</p>
                <p><strong>Email:</strong> hellocarekonnect@gmail.com</p>
                <p><strong>Mail:</strong></p>
                <div className="ml-4 text-sm text-muted-foreground">
                  <p>CareKonnect Privacy Office</p>
                  <p>Minnesota Department of Health</p>
                  <p>625 Robert Street North</p>
                  <p>St. Paul, MN 55155</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;